import { getTokens } from '../auth-storage';
import { z } from 'zod';
import { apiRequest, readPage, referenceRecords } from '../masterdata/repository';
import { entityById } from '../masterdata/catalog';
import { recordLabel } from '../masterdata/domain';
import { purchaseOrders } from '../procurement/repository';

export type ReferenceKind =
  | 'employee'
  | 'account'
  | 'purchase-line'
  | 'shipment-line'
  | 'lot'
  | `masterdata:${string}`
  | keyof typeof operationalReferences;
export type ReferenceDetail = { label: string; href?: string };

// Only detail routes present in the reviewed release. Scoped records require their
// parent shipment; an ID is never used to guess a different resource's identity.
const operationalReferences = {
  shipment: {
    path: '/shipment-tracking/shipments/{id}',
    fields: ['shipment_number'],
    href: '/admin/shipments?record={id}',
  },
  invoice: {
    path: '/billing/invoices/{id}',
    fields: ['invoice_number', 'document_kind', 'invoice_type', 'currency_code', 'issue_date'],
    href: '/admin/billing?view=invoices&record={id}',
  },
  period: {
    path: '/billing/periods/{id}',
    fields: ['cycle', 'period_start', 'period_end'],
    href: '/admin/billing?view=periods&record={id}',
  },
  run: {
    path: '/billing/runs/{id}',
    fields: ['run_number', 'status', 'started_at'],
    href: '/admin/billing?view=runs&record={id}',
  },
  report: {
    path: '/warehouse-monitoring/reports/{id}',
    fields: ['warehouse.name', 'report_type', 'business_date', 'file.file_name'],
    href: '/admin/warehouse?view=reports&record={id}',
  },
  'receipt-group': {
    path: '/warehouse-monitoring/receipt-groups/{id}',
    fields: ['rinchem_order_no', 'shipment_number', 'status'],
    href: '/admin/warehouse?view=receipts&record={id}',
  },
  movement: {
    path: '/warehouse-monitoring/movements/{id}',
    fields: ['shipment_reference', 'carrier_text', 'ship_to_account', 'status'],
    href: '/admin/warehouse?view=movements&record={id}',
  },
  discrepancy: {
    path: '/warehouse-monitoring/discrepancies/{id}',
    fields: ['product_code', 'lot_code', 'discrepancy_type', 'status'],
    href: '/admin/warehouse?view=discrepancies&record={id}',
  },
  'storage-import': {
    path: '/temperature-compliance/storage-room-imports/{id}',
    fields: ['factory_code', 'file.file_name', 'created_at'],
    href: '/admin/temperature?view=storage&record={id}',
  },
  'plant-record': {
    path: '/temperature-compliance/plant-monitoring-records/{id}',
    fields: ['factory_code', 'file.file_name', 'covers.from', 'covers.to'],
    href: '/admin/temperature?view=plant&record={id}',
  },
  'cost-invoice': {
    path: '/billing/logistics-costs/{id}',
    fields: ['vendor_invoice_number', 'carrier_name', 'invoice_date'],
    href: '/admin/billing?view=costs&record={id}',
  },
  'cost-breakdown': {
    path: '/billing/cost-breakdowns/{id}',
    fields: ['description', 'due_to', 'status'],
    href: '/admin/billing?view=breakdowns&record={id}',
  },
  payable: {
    path: '/billing/payables/{id}',
    fields: ['vendor_invoice_number', 'payable_type', 'invoice_date'],
    href: '/admin/billing?view=payables&record={id}',
  },
  statement: {
    path: '/billing/tariff-statements/{id}',
    fields: ['version', 'status', 'created_at'],
    href: '/admin/billing?view=statements&record={id}',
  },
  logger: {
    path: '/shipment-tracking/shipments/{scope}/temperature/logger-files/{id}',
    fields: ['serial_number', 'box_reference', 'file.file_name'],
    href: '/admin/temperature?view=loggers&record={id}&shipment_id={scope}',
  },
  evaluation: {
    path: '/shipment-tracking/shipments/{scope}/temperature/evaluations/{id}',
    fields: ['run_number', 'verdict', 'evaluated_at'],
    href: '/admin/temperature?view=evaluations&record={id}&shipment_id={scope}',
  },
} satisfies Record<string, { path: string; fields: string[]; href: string }>;
function property(value: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (item, key) =>
        item && typeof item === 'object' ? (item as Record<string, unknown>)[key] : undefined,
      value
    );
}

/** A page-owned cache. Never share identity data between authenticated sessions. */
export function createReferenceDetails(accessToken = getTokens()?.accessToken) {
  const requests = new Map<string, Promise<unknown>>();
  function once<T>(key: string, load: () => Promise<T>): Promise<T> {
    if (getTokens()?.accessToken !== accessToken)
      return Promise.reject(new Error('Session changed'));
    const previous = requests.get(key);
    if (previous) return previous as Promise<T>;
    const result = load().then((value) => {
      if (getTokens()?.accessToken !== accessToken) throw new Error('Session changed');
      return value;
    });
    requests.set(key, result);
    return result;
  }
  const signal = new AbortController().signal;
  const users = z.object({
    data: z.array(
      z.object({
        id: z.string(),
        sub: z.string().nullable(),
        email: z.string(),
        givenName: z.string().nullable(),
        familyName: z.string().nullable(),
      })
    ),
    total: z.number(),
  });
  async function resolve(
    kind: ReferenceKind,
    id: string,
    scope?: string
  ): Promise<ReferenceDetail> {
    if (kind.startsWith('masterdata:')) {
      const entity = kind.slice('masterdata:'.length);
      const rows = await once(`masterdata:${entity}`, () => referenceRecords(entity));
      let row = rows.find((item) => item.id === id);
      if (!row) {
        const base = `/${entity}`;
        const route = entityById(entity)?.routes.find(
          (item) => item.method === 'get' && new RegExp(`^${base}/:[^/]+$`).test(item.path)
        );
        if (route)
          row = (
            await once(`master-record:${entity}:${id}`, () =>
              readPage(`${base}/${encodeURIComponent(id)}`)
            )
          ).data.find((item) => item.id === id);
      }
      if (!row) throw new Error('Reference details unavailable');
      return { label: recordLabel(row, entity) };
    }
    if (kind in operationalReferences) {
      const definition = operationalReferences[kind as keyof typeof operationalReferences];
      if (definition.path.includes('{scope}') && !scope)
        throw new Error('Parent shipment unavailable');
      const fill = (template: string) =>
        template
          .replaceAll('{id}', encodeURIComponent(id))
          .replaceAll('{scope}', encodeURIComponent(scope ?? ''));
      const row = await once(`resource:${fill(definition.path)}`, () =>
        apiRequest(fill(definition.path))
      );
      const parts = definition.fields.flatMap((key) => {
        const value = property(row, key);
        if (typeof value !== 'string' && typeof value !== 'number') return [];
        if (value === '') return [];
        return [
          key === 'run_number'
            ? `Run ${value}`
            : key === 'version'
              ? `Version ${value}`
              : [
                    'cycle',
                    'status',
                    'verdict',
                    'document_kind',
                    'invoice_type',
                    'report_type',
                    'discrepancy_type',
                    'payable_type',
                  ].includes(key)
                ? String(value).replaceAll('_', ' ')
                : String(value),
        ];
      });
      if (!parts.length) throw new Error('Reference details unavailable');
      return { label: parts.join(' · '), href: fill(definition.href) };
    }
    if (kind === 'employee') {
      const page = await readPage(`/employees/${encodeURIComponent(id)}`);
      const row = page.data.find((item) => item.id === id);
      const name = typeof row?.employee_name === 'string' ? row.employee_name.trim() : '';
      if (!name) throw new Error('Employee name unavailable');
      return { label: name };
    }
    if (kind === 'account') {
      for (let page = 1, seen = 0; ; page++) {
        const result = await once(`users:${page}`, async () =>
          users.parse(await apiRequest(`/admin/users?page=${page}&limit=100`))
        );
        // Shipment actors are login subjects, not employee IDs. Match exactly, never by email.
        const user = result.data.find((row) => row.sub === id || row.id === id);
        if (user)
          return {
            label: [user.givenName, user.familyName].filter(Boolean).join(' ').trim() || user.email,
          };
        seen += result.data.length;
        if (!result.data.length || seen >= result.total) break;
      }
    }
    if (kind === 'lot') {
      const lot = z
        .object({ batch_code: z.string() })
        .parse(await apiRequest(`/shipment-tracking/batch-lots/${encodeURIComponent(id)}`));
      return { label: lot.batch_code };
    }
    if (kind === 'shipment-line') {
      if (!scope) throw new Error('Parent shipment unavailable');
      const detail = await once(
        `resource:/shipment-tracking/shipments/${encodeURIComponent(scope)}`,
        () => apiRequest(`/shipment-tracking/shipments/${encodeURIComponent(scope)}`)
      );
      const lines = property(detail, 'lines');
      const line = Array.isArray(lines)
        ? lines.find((item: unknown) => property(item, 'id') === id)
        : undefined;
      if (!line) throw new Error('Shipment line unavailable');
      const parts = ['product_code', 'lot_code', 'pallet_serial']
        .map((key) => property(line, key))
        .filter((value): value is string => typeof value === 'string' && Boolean(value));
      if (!parts.length) throw new Error('Shipment line details unavailable');
      return {
        label: parts.join(' · '),
        href: `/admin/shipments?record=${encodeURIComponent(scope)}`,
      };
    }
    if (kind === 'purchase-line') {
      // The deployed contract has no PO-line detail endpoint. Share paged PO reads and
      // details across visible references, stop on the matching line, never invent a route.
      for (let page = 1, seen = 0; ; page++) {
        const result = await once(`purchase-page:${page}`, () =>
          purchaseOrders.list(new URLSearchParams({ page: String(page), pageSize: '100' }), signal)
        );
        for (const po of result.data) {
          const detail = await once(`purchase:${po.id}`, () =>
            purchaseOrders.detail(po.id, signal)
          );
          const line = detail.lines.find((row) => row.id === id);
          if (line)
            return {
              label: `${po.po_number} / ${line.line_number}`,
              href: `/admin/procurement?record=${encodeURIComponent(po.id)}`,
            };
        }
        seen += result.data.length;
        if (!result.data.length || seen >= result.total) break;
      }
    }
    throw new Error('Reference details unavailable');
  }
  return {
    get(kind: ReferenceKind, id: string, scope?: string) {
      return once(`${kind}:${scope ?? ''}:${id}`, () => resolve(kind, id, scope));
    },
  };
}
